import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fmtUSD } from "../../lib/format";
import { Switch } from "../ui/switch";

export default function CommissionComparison({ result, inputs, setInputs }) {
  if (!result) return null;
  const cc = result.commission_comparison;
  const price = Number(inputs?.sale_price) || 0;
  const eqFee = price * 0.01;
  const dsFee = price * 0.02;
  const listing3 = price * 0.03;
  const tradLow = price * 0.055;
  const tradHigh = price * 0.06;
  const maxFee = Math.max(listing3, dsFee, eqFee, tradHigh) || 1;
  const listingDiff = listing3 - eqFee;          // primary: traditional 3% listing − Equitas 1% listing
  const totalSaveLow = tradLow - price * 0.035;   // vs 5.5% total (Equitas 1% + 2.5% buyer = 3.5%)
  const totalSaveHigh = tradHigh - price * 0.035; // vs 6.0% total

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
      className="rounded-2xl bg-brand-emerald text-brand-cream p-6 sm:p-8" data-testid="commission-comparison-card">
      <span className="eyebrow">Commission Comparison</span>
      <h2 className="mt-2 font-serif text-2xl sm:text-3xl font-light">Keep more of your equity</h2>

      <div className="mt-6 space-y-4">
        <Bar label="Equitas at 1%" amount={eqFee} max={maxFee} highlight />
        <Bar label="Equitas 2% Direct Sale" amount={dsFee} max={maxFee} highlight tone="cream"
          sub="1% listing + 1% unrepresented-buyer coordination · single agency" testid="commission-bar-direct-sale" />
        <Bar label="Illustrative 3% Listing Fee (listing side only)" amount={listing3} max={maxFee} diff={listingDiff} testid="commission-bar-listing-3" />
        <RangeBar label="Illustrative Traditional Total (5.5% – 6% listing + buyer side)" low={tradLow} high={tradHigh} max={maxFee} testid="commission-bar-traditional-total" />
      </div>

      <div className="mt-6 rounded-xl bg-brand-dark/50 p-4">
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-brand-cream/70">Potential listing-fee difference</span>
          <span className="font-mono text-xl font-bold text-brand-gold" data-testid="commission-savings-amount">{fmtUSD(listingDiff)}</span>
        </div>
        <p className="mt-2 text-xs text-brand-cream/60 leading-relaxed" data-testid="commission-total-savings-note">
          Total savings of {fmtUSD(totalSaveLow)} – {fmtUSD(totalSaveHigh)} compared to a traditional 5.5% – 6% total commission (assuming standard 2.5% buyer-broker compensation).
        </p>
      </div>

      {cc.direct_sale && (
        <Link to="/2-percent-direct-sale" data-testid="cc-direct-sale-link"
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-gold hover:underline">
          Selling to an unrepresented buyer? See the 2% Direct Sale →
        </Link>
      )}

      {/* Scenario B */}
      <div className="mt-6 rounded-xl border border-brand-cream/15 p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="font-medium">Equitas represents both seller &amp; buyer</p>
            <p className="text-xs text-brand-cream/60 mt-1">1% seller + 1% buyer = {cc.dual_agency.combined_pct}% combined ({fmtUSD(cc.dual_agency.fee)})</p>
          </div>
          <Switch checked={!!inputs.dual_agency} onCheckedChange={(v) => setInputs((p) => ({ ...p, dual_agency: v }))} data-testid="dual-agency-switch"
            className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-brand-cream/30" />
        </div>
        {inputs.dual_agency && (
          <div className="mt-3 rounded-lg bg-emerald-500/15 border border-emerald-400/30 p-3 text-xs" data-testid="dual-agency-active-note">
            <p className="font-semibold text-emerald-200">Dual-agency scenario applied to your net sheet</p>
            <p className="mt-1 text-brand-cream/80">
              Buyer-side commission is now 1% instead of {inputs.buyer_side_pct}%. Total commission: <span className="font-mono">{fmtUSD(result.totals?.seller_commissions)}</span> ({cc.dual_agency.combined_pct}% combined).
            </p>
          </div>
        )}
        <p className="mt-3 text-xs text-brand-cream/60 leading-relaxed">
          Applies only if Equitas actually represents both parties and the arrangement is permitted, disclosed, and agreed to with the required informed written consent. A buyer represented by Equitas is not unrepresented.
        </p>
      </div>

      <p className="mt-5 text-xs text-brand-cream/50 leading-relaxed">
        Comparison figures are illustrative benchmarks — all real estate commissions, broker compensations, and fees are fully negotiable and not set by law. Buyer-side compensation is determined independently by the seller. Dual agency and direct sale arrangements are subject to applicable agreements and statutory disclosure requirements.
      </p>
    </motion.div>
  );
}

const RangeBar = ({ label, low, high, max, testid }) => (
  <div data-testid={testid}>
    <div className="flex justify-between text-sm mb-1">
      <span className="text-brand-cream/80">{label}</span>
      <span className="font-mono">{fmtUSD(low)} – {fmtUSD(high)}</span>
    </div>
    <div className="h-2.5 rounded-full bg-brand-dark/50 overflow-hidden">
      <div className="h-full rounded-full bg-brand-cream/30" style={{ width: `${(high / max) * 100}%` }} />
    </div>
  </div>
);

const Bar = ({ label, amount, max, diff, highlight, tone = "gold", sub, testid }) => (
  <div data-testid={testid}>
    <div className="flex justify-between text-sm mb-1">
      <span className={highlight ? (tone === "cream" ? "text-brand-cream font-semibold" : "text-brand-gold font-semibold") : "text-brand-cream/80"}>{label}</span>
      <span className="font-mono">{fmtUSD(amount)}</span>
    </div>
    <div className="h-2.5 rounded-full bg-brand-dark/50 overflow-hidden">
      <div className={`h-full rounded-full ${highlight ? (tone === "cream" ? "bg-brand-cream/70" : "bg-brand-gold") : "bg-brand-cream/40"}`} style={{ width: `${(amount / max) * 100}%` }} />
    </div>
    {sub && <p className="mt-1 text-xs text-brand-cream/50">{sub}</p>}
    {diff !== undefined && <p className="mt-1 text-xs text-brand-cream/50">You keep {fmtUSD(diff)} more with Equitas</p>}
  </div>
);
